/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { Paper, Table, TableBody, TableCell, TableRow, Tooltip } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import { StyledInputLabel } from 'components/widgets'
import type { VoiceLicensedUser } from 'models/api/licenses'

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 520,
  marginTop: 20,
})

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 24,
})

const InfoTrigger = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 0,
  cursor: 'pointer',
  transform: 'translateY(-1px)',
  color: 'var(--color-primary)',
  opacity: 0.8,
})

const cellStyle: React.CSSProperties = {
  fontFamily: 'Montserrat',
  fontSize: 12,
  padding: '5px 12px',
  color: 'var(--color-text)',
}

const placeholderStyle: React.CSSProperties = { ...cellStyle, color: 'var(--color-text-muted)', fontStyle: 'italic' }

type Props = {
  // Undefined while the org's licenses load.
  users?: VoiceLicensedUser[]
  title: string
  info: string
  emptyText: string
  loadingText: string
}

// Lists the users holding a voice license beside the Devices table, since a
// user's voice isn't tied to any one alert source row.
export const VoiceLicensedUsers = ({ users, title, info, emptyText, loadingText }: Props) => (
  <Wrapper>
    <HeaderRow>
      <StyledInputLabel>{title}</StyledInputLabel>
      <Tooltip title={<span>{info}</span>} placement="right" arrow>
        <InfoTrigger role="button" tabIndex={0} aria-label={info}>
          <InfoOutlinedIcon fontSize="small" style={{ display: 'block', color: 'inherit', width: '20px' }} />
        </InfoTrigger>
      </Tooltip>
    </HeaderRow>

    <Paper elevation={1} style={{ maxWidth: 400, maxHeight: 360, overflowY: 'auto', marginTop: 8 }}>
      <Table size="small" style={{ tableLayout: 'fixed' }}>
        <TableBody>
          {!users && (
            <TableRow>
              <TableCell style={placeholderStyle}>{loadingText}</TableCell>
            </TableRow>
          )}

          {users?.length === 0 && (
            <TableRow>
              <TableCell style={placeholderStyle}>{emptyText}</TableCell>
            </TableRow>
          )}

          {users?.map(user => (
            <TableRow key={user.userId} hover>
              <TableCell style={{ ...cellStyle, wordBreak: 'break-all' }}>{user.label}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  </Wrapper>
)
